import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { getDietaryProducts, getDietaryProductMeals, createDietaryProduct, updateDietaryProduct, deleteDietaryProduct, saveDietaryProductMeals } from '../api';

// 产品类型配置
const PRODUCT_TYPE_CONFIG = {
  '7day': {
    icons: ['📦', '🥗', '🍱', '🍲', '🥣'],
    descriptions: ['7天膳食包是根据中医体质理论精心配制的营养套餐，通过科学配比实现温和调理'],
    template: {
      introduction: '📌 7天膳食包是根据中医体质理论精心配制的营养套餐，通过7天科学膳食搭配，帮助调理体质、增强免疫力、实现健康减重。',
      daily_meals: [
        { day: 1, breakfast: '燕麦粥 + 水煮蛋 + 凉拌黄瓜', lunch: '糙米饭 + 清蒸鲈鱼 + 蒜蓉西兰花', dinner: '杂粮饭 + 番茄豆腐汤 + 凉拌海带丝' }
      ],
      nutrition_ratio: { carbohydrate: '45%', protein: '25%', fat: '15%', fiber: '15%' },
      suitable_types: ['气虚体质', '痰湿体质', '湿热体质'],
      nutrition_tags: ['高蛋白', '高纤维', '低脂'],
      precautions: ['孕妇禁用', '过敏体质慎用', '保质期7天']
    }
  },
  '21day': {
    icons: ['🥗', '🎯', '🏃', '💪', '📊'],
    descriptions: ['21天科学减脂餐，通过合理配比实现健康减重，不节食、不伤代谢、温和减脂'],
    template: {
      introduction: '📌 21天科学减脂餐，通过合理配比实现健康减重...',
      phase_goals: [
        { phase: '第1-7天', goal: '适应期', weight_loss: '1-2kg' },
        { phase: '第8-14天', goal: '减脂期', weight_loss: '2-3kg' },
        { phase: '第15-21天', goal: '巩固期', weight_loss: '1-2kg' }
      ],
      suitable_types: ['痰湿体质', '湿热体质', '单纯性肥胖'],
      nutrition_tags: ['低脂', '高蛋白', '高纤维'],
      precautions: ['孕妇禁用', '慢性病患者慎用']
    }
  },
  'meal': {
    icons: ['🍱', '🥣', '🥤', '🍵'],
    descriptions: ['营养代餐，方便快捷补充蛋白质和维生素'],
    template: {
      introduction: '📌 营养代餐，方便快捷补充蛋白质和维生素...',
      nutrition_facts: { protein: '25g', calories: '180kcal', fat: '6g', carbohydrate: '15g', fiber: '5g' },
      suitable_types: ['单纯性肥胖', '久坐办公'],
      nutrition_tags: ['高蛋白', '低热量', '低脂'],
      precautions: ['不可完全替代正餐', '孕妇慎用']
    }
  },
  'tea': {
    icons: ['🍵', '🫖', '🌿', '🍃'],
    descriptions: ['传统养生茶饮，调理体质，养生保健'],
    template: {
      introduction: '📌 传统养生茶饮，调理体质...',
      ingredients: [
        { name: '赤小豆', effect: '利水消肿' },
        { name: '薏苡仁', effect: '健脾祛湿' }
      ],
      suitable_types: ['痰湿体质', '湿热体质'],
      nutrition_tags: ['无糖', '天然草本'],
      precautions: ['孕妇禁用', '儿童减量']
    }
  },
  'supplement': {
    icons: ['💊', '💉', '🩺'],
    descriptions: ['科学配比的营养补充剂'],
    template: {
      introduction: '📌 科学配比的营养补充剂...',
      nutrition_ingredients: [
        { name: '维生素C', content: '100mg', benefit: '增强免疫力' }
      ],
      suitable_types: ['体质虚弱', '加班族'],
      nutrition_tags: ['多种维生素', '矿物质'],
      precautions: ['不可超量服用', '孕妇慎用']
    }
  }
};

const DIETARY_TYPES = ['全部', '7day', '21day', 'meal', 'tea', 'supplement'];
const TYPE_LABELS = {
  '全部': '全部类型', '7day': '7天膳食包', '21day': '21天减脂餐',
  'meal': '代餐系列', 'tea': '养生茶饮', 'supplement': '营养补充'
};

const ALL_EMOJIS = ['📦', '🥗', '🍱', '🍲', '🥣', '🍚', '🥦', '🥕', '🍜', '🍛', '🍝', '🍵', '🫖', '🌿', '🍃', '☕', '💊', '💉', '🩺', '🎯', '🏃', '💪', '📊', '📈', '🏆', '✨'];

// 膳食产品图标库（两套：web用48px，app用128px）
const DIETARY_ICON_LIBRARY = [
  { id: 1, web: 'https://img.shanxiaoqing.cn/images/mall/mall-icon-1-48.png', app: 'https://img.shanxiaoqing.cn/images/mall/mall-icon-1-128.png' },
  { id: 2, web: 'https://img.shanxiaoqing.cn/images/mall/mall-icon-2-48.png', app: 'https://img.shanxiaoqing.cn/images/mall/mall-icon-2-128.png' },
  { id: 3, web: 'https://img.shanxiaoqing.cn/images/mall/mall-icon-3-48.png', app: 'https://img.shanxiaoqing.cn/images/mall/mall-icon-3-128.png' },
  { id: 4, web: 'https://img.shanxiaoqing.cn/images/mall/mall-icon-4-48.png', app: 'https://img.shanxiaoqing.cn/images/mall/mall-icon-4-128.png' },
];

function Dietary() {
  const user = JSON.parse(sessionStorage.getItem('health_user') || '{}');
  const isAdmin = user?.role === 'admin';
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('全部');
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showTemplateHelp, setShowTemplateHelp] = useState(false);
  const [showNutritionModal, setShowNutritionModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [nutritionData, setNutritionData] = useState([]);
  const [formData, setFormData] = useState({
    title: '', description: '', price: '', icon: '', icon_app: '', product_type: '', store_id: '', rich_description: '', nutrition_data: ''
  });
  const [showNutritionHelp, setShowNutritionHelp] = useState(false);
  const [stores, setStores] = useState([]);
  const richDescRef = useRef(null);

  useEffect(() => { fetchProducts(); fetchStores(); }, [filterType]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const res = await getDietaryProducts({ type: filterType === '全部' ? '' : filterType });
      if (res.data.success) setProducts(res.data.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchStores = async () => {
    try {
      const res = await axios.get('/api/stores');
      if (res.data.success) setStores(res.data.data || []);
    } catch (e) { console.error(e); }
  };

  const handleTypeChange = (newType) => {
    setFormData({ ...formData, product_type: newType === '全部' ? '' : newType });
    if (newType && newType !== '全部' && PRODUCT_TYPE_CONFIG[newType]) {
      const config = PRODUCT_TYPE_CONFIG[newType];
      // 使用网络图片而不是emoji
      const defaultIcon = DIETARY_ICON_LIBRARY[0] || { web: '', app: '' };
      setTimeout(() => {
        setFormData(prev => ({
          ...prev,
          icon: prev.icon || defaultIcon.web,
          icon_app: prev.icon_app || defaultIcon.app,
          description: prev.description || config.descriptions[0],
          rich_description: JSON.stringify(config.template, null, 2)
        }));
      }, 0);
    }
  };

  // 选择图标（同时设置web和app图标）
  const handleSelectIcon = (iconItem) => {
    setFormData({ ...formData, icon: iconItem.web, icon_app: iconItem.app });
    setShowIconPicker(false);
  };

  const insertEmoji = (emoji) => {
    const textarea = richDescRef.current;
    if (!textarea) return;
    const startPos = textarea.selectionStart;
    const endPos = textarea.selectionEnd;
    const newValue = (formData.rich_description || '').substring(0, startPos) + emoji + (formData.rich_description || '').substring(endPos);
    setFormData({ ...formData, rich_description: newValue });
    setTimeout(() => { textarea.focus(); textarea.setSelectionRange(startPos + emoji.length, startPos + emoji.length); }, 0);
  };

  const handleEdit = async (product) => {
    setEditItem(product);
    let richDesc = '';
    if (product.rich_description) {
      richDesc = typeof product.rich_description === 'string' ? product.rich_description : JSON.stringify(product.rich_description, null, 2);
    }

    // 加载营养数据
    let nutritionDataStr = '';
    try {
      const res = await getDietaryProductMeals(product.id);
      if (res.data.success && res.data.data && res.data.data.length > 0) {
        // 将API返回的营养数据转换为前端格式
        const mealsByDay = {};
        res.data.data.forEach(meal => {
          if (!mealsByDay[meal.day_number]) {
            mealsByDay[meal.day_number] = [];
          }
          mealsByDay[meal.day_number].push({
            type: meal.meal_type,
            content: meal.meal_content,
            calories: meal.calories,
            protein: meal.protein,
            carbs: meal.carbs,
            fat: meal.fat,
            fiber: meal.fiber
          });
        });

        // 转换为前端需要的格式
        const days = Object.keys(mealsByDay).map(dayNum => ({
          day: parseInt(dayNum),
          meals: mealsByDay[dayNum]
        }));

        nutritionDataStr = JSON.stringify({ days }, null, 2);
      }
    } catch (e) {
      console.error('加载营养数据失败:', e);
    }

    setFormData({
      title: product.title || '',
      description: product.description || '',
      price: product.price || '',
      icon: product.icon || '',
      icon_app: product.icon_app || '',
      product_type: product.product_type || '',
      store_id: product.store_id || '',
      rich_description: richDesc,
      nutrition_data: nutritionDataStr
    });
    setShowModal(true);
  };

  const handleViewNutrition = async (product) => {
    setSelectedProduct(product);
    setShowNutritionModal(true);
    try {
      const res = await getDietaryProductMeals(product.id);
      if (res.data.success) setNutritionData(res.data.data);
    } catch (e) { console.error(e); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let richDesc = null;
      if (formData.rich_description && formData.rich_description.trim()) {
        try {
          richDesc = JSON.parse(formData.rich_description);
        } catch (err) {
          alert('JSON格式错误，请检查！');
          return;
        }
      }
      const data = {
        title: formData.title,
        description: formData.description,
        price: parseFloat(formData.price) || 0,
        icon: formData.icon,
        icon_app: formData.icon_app,
        product_type: formData.product_type,
        category: formData.product_type,
        store_id: isAdmin ? (formData.store_id || null) : (user.store_id || null),
        rich_description: richDesc
      };
      let productId;
      if (editItem) {
        await updateDietaryProduct(editItem.id, data);
        productId = editItem.id;
      } else {
        const result = await createDietaryProduct(data);
        productId = result.data?.data?.id;
      }

      // 保存营养数据到meal_templates表
      if (formData.nutrition_data && formData.nutrition_data.trim() && productId) {
        try {
          const nutritionData = JSON.parse(formData.nutrition_data);
          if (nutritionData.days && Array.isArray(nutritionData.days)) {
            const meals = [];
            for (const day of nutritionData.days) {
              if (day.meals && Array.isArray(day.meals)) {
                for (const meal of day.meals) {
                  meals.push({
                    day_number: day.day,
                    meal_type: meal.type,
                    meal_content: meal.content,
                    calories: parseFloat(meal.calories) || 0,
                    protein: parseFloat(meal.protein) || 0,
                    carbs: parseFloat(meal.carbs) || 0,
                    fat: parseFloat(meal.fat) || 0,
                    fiber: parseFloat(meal.fiber) || 0
                  });
                }
              }
            }
            if (meals.length > 0) {
              await saveDietaryProductMeals(productId, meals);
            }
          }
        } catch (err) {
          console.error('保存营养数据失败:', err);
        }
      }

      setShowModal(false);
      resetForm();
      fetchProducts();
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('确定删除?')) {
      try {
        await deleteDietaryProduct(id);
        fetchProducts();
      } catch (err) {
        alert(err.response?.data?.message || '删除失败');
      }
    }
  };

  const resetForm = () => {
    setEditItem(null);
    setFormData({ title: '', description: '', price: '', icon: '', icon_app: '', product_type: '', store_id: '', rich_description: '', nutrition_data: '' });
    setShowIconPicker(false);
    setShowTemplateHelp(false);
    setShowNutritionHelp(false);
  };

  return (
    <div className="content-card">
      <div className="section-title">🥗 膳食产品管理</div>
      <div style={{ marginBottom: 12 }}>
        <button className="action-btn btn-blue" onClick={() => { resetForm(); setShowModal(true); }}>+ 新增产品</button>
      </div>
      <div className="filter-bar">
        <select className="filter-select" value={filterType} onChange={e => setFilterType(e.target.value)}>
          {DIETARY_TYPES.map(t => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
        </select>
      </div>

      {loading ? <div className="loading-text">加载中...</div> : (
        <table className="basic-table">
          <thead>
            <tr key="header"><th>序号</th><th>门店</th><th>图标</th><th>名称</th><th>类型</th><th>价格</th><th>描述</th><th>操作</th></tr>
          </thead>
          <tbody>
            {(() => {
              const emptyRow = <tr><td colSpan={8} style={{textAlign:'center',color:'#999'}}>暂无数据</td></tr>;
              if (products.length === 0) return emptyRow;
              return products.map((p, i) => (
                <tr key={p.id}>
                  <td className="no-cell">{i + 1}</td>
                  <td>{p.store_name || '-'}</td>
                  <td>{p.icon ? <img src={p.icon} alt="" style={{width: '32px', height: '32px', objectFit: 'contain'}} /> : '📦'}</td>
                  <td>{p.title}</td>
                  <td>{TYPE_LABELS[p.product_type] || '-'}</td>
                  <td>¥{p.price}</td>
                  <td>{(p.description || '-').substring(0, 25)}</td>
                  <td>
                    <button className="btn-xs btn-blue" style={{marginRight: 4}} onClick={() => handleEdit(p)}>编辑</button>
                    <button className="btn-xs btn-green" style={{marginRight: 4}} onClick={() => handleViewNutrition(p)}>营养</button>
                    <button className="btn-xs btn-red" onClick={() => handleDelete(p.id)}>删除</button>
                  </td>
                </tr>
              ));
            })()}
          </tbody>
        </table>
      )}

      {/* 产品编辑弹窗 */}
      {showModal && (
        <div className="modal-mask">
          <div className="modal-box" onClick={e => e.stopPropagation()} style={{maxWidth: '800px', maxHeight: '90vh', overflow: 'auto'}}>
            <div className="modal-title">
              <span>{editItem ? '编辑' : '新增'}膳食产品</span>
              <span className="modal-close" onClick={() => setShowModal(false)}>×</span>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-content">
                <div className="form-tip">带 <span style={{color:'#ff4d4f'}}>*</span> 号的为必填项</div>

                <div className="form-card">
                  <div className="form-card-title">📋 基本信息</div>
                  <div className="form-row">
                    {isAdmin && (
                      <div className="form-field" style={{flex: 1}}>
                        <label className="form-label">所属门店 *</label>
                        <select className="form-input" value={formData.store_id} onChange={e => setFormData({...formData, store_id: e.target.value})} required>
                          <option value="">请选择</option>
                          {stores.map(store => <option key={store.id} value={store.id}>{store.name}</option>)}
                        </select>
                      </div>
                    )}
                    <div className="form-field" style={{flex: 2}}>
                      <label className="form-label">产品名称 *</label>
                      <input className="form-input" value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} placeholder="例如：膳小卿·21天减脂餐" required />
                    </div>
                    <div className="form-field" style={{flex: 1}}>
                      <label className="form-label">价格 *</label>
                      <input className="form-input" type="number" value={formData.price} onChange={e => setFormData({...formData, price: e.target.value})} placeholder="价格" required />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-field" style={{flex: 1}}>
                      <label className="form-label">类型 *</label>
                      <select className="form-input" value={formData.product_type} onChange={e => handleTypeChange(e.target.value)}>
                        <option value="">请选择</option>
                        {DIETARY_TYPES.slice(1).map(t => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
                      </select>
                    </div>
                    <div className="form-field" style={{flex: 1}}>
                      <label className="form-label">图标</label>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        {formData.icon && (
                          <img src={formData.icon} alt="已选图标" style={{ width: '32px', height: '32px', borderRadius: '4px', border: '1px solid #ddd' }} />
                        )}
                        <button type="button" className="btn-xs btn-blue" onClick={() => setShowIconPicker(!showIconPicker)}>{formData.icon ? '更换' : '选择'}</button>
                      </div>
                      {showIconPicker && (
                        <div style={{ border: '1px solid #ddd', marginTop: '10px', padding: '10px', borderRadius: '8px', backgroundColor: '#fafafa' }}>
                          <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>点击选择图标：</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            {DIETARY_ICON_LIBRARY.map((item) => (
                              <button
                                key={item.id}
                                type="button"
                                onClick={() => handleSelectIcon(item)}
                                style={{ padding: '4px', border: formData.icon === item.web ? '2px solid #1890ff' : '1px solid #ddd', borderRadius: '6px', cursor: 'pointer', background: '#fff' }}
                              >
                                <img src={item.web} alt={`图标${item.id}`} style={{ width: '40px', height: '40px', objectFit: 'contain' }} />
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-field">
                      <label className="form-label">简短描述</label>
                      <input className="form-input" value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} placeholder="产品一句话描述" />
                    </div>
                  </div>
                </div>

                <div className="form-card">
                  <div className="form-card-title">💾 详细描述</div>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                    <button type="button" className="btn-xs btn-green" onClick={() => setShowTemplateHelp(!showTemplateHelp)}>📋 格式帮助</button>
                    <button type="button" className="btn-xs btn-blue" onClick={() => setShowEmojiPicker(!showEmojiPicker)}>😊 插入图标</button>
                  </div>

                  {showEmojiPicker && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px', padding: '10px', border: '1px solid #4CAF50', borderRadius: '8px', maxHeight: '150px', overflowY: 'auto', backgroundColor: '#f0f9f0' }}>
                      {ALL_EMOJIS.map((emoji, idx) => (
                        <button key={idx} type="button" onClick={() => insertEmoji(emoji)} style={{ fontSize: '18px', padding: '3px 5px', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer', background: '#fff' }}>{emoji}</button>
                      ))}
                    </div>
                  )}

                  <textarea ref={richDescRef} className="json-input" rows={15} value={formData.rich_description} onChange={e => setFormData({...formData, rich_description: e.target.value})} placeholder={'{\n  "introduction": "产品介绍...",\n  "suitable_types": ["气虚体质"],\n  "nutrition_tags": ["高蛋白"],\n  "precautions": ["注意事项..."]\n}'} />

                  {showTemplateHelp && (
                    <div style={{ border: '1px solid #e0e0e0', borderRadius: '8px', padding: '16px', backgroundColor: '#f8f9fa', marginTop: '12px' }}>
                      <div style={{ fontSize: '14px', color: '#333', marginBottom: '12px', fontWeight: 'bold' }}>📋 支持的字段说明：</div>

                      <div style={{ fontSize: '12px', lineHeight: '2', color: '#555' }}>
                        <div><span style={{color:'#1890ff',fontWeight:'bold'}}>📌 introduction</span> - 产品介绍/简介</div>
                        <div style={{marginLeft:'20px',color:'#888',fontSize:'11px'}}>{"{ \"introduction\": \"📌 7天膳食包是根据中医体质理论...\" }"}</div>

                        <div><span style={{color:'#1890ff',fontWeight:'bold'}}>🏷️ suitable_types</span> - 适合体质（数组）</div>
                        <div style={{marginLeft:'20px',color:'#888',fontSize:'11px'}}>{"{ \"suitable_types\": [\"气虚体质\", \"痰湿体质\"] }"}</div>

                        <div><span style={{color:'#1890ff',fontWeight:'bold'}}>🥗 nutrition_tags</span> - 营养标签（数组）</div>
                        <div style={{marginLeft:'20px',color:'#888',fontSize:'11px'}}>{"{ \"nutrition_tags\": [\"高蛋白\", \"高纤维\"] }"}</div>

                        <div><span style={{color:'#1890ff',fontWeight:'bold'}}>⚠️ precautions</span> - 注意事项（数组）</div>
                        <div style={{marginLeft:'20px',color:'#888',fontSize:'11px'}}>{"{ \"precautions\": [\"孕妇禁用\", \"过敏体质慎用\"] }"}</div>
                      </div>
                    </div>
                  )}
                </div>

                {/* 营养数据输入 */}
                <div className="form-card" style={{marginTop: '16px'}}>
                  <div className="form-card-title">📊 营养数据（每日餐次）</div>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                    <button type="button" className="btn-xs btn-green" onClick={() => setShowNutritionHelp(!showNutritionHelp)}>📋 格式帮助</button>
                    <button type="button" className="btn-xs btn-blue" onClick={() => setFormData({...formData, nutrition_data: '{\n  "days": [\n    { "day": 1, "meals": [\n      { "type": "breakfast", "content": "燕麦粥+水煮蛋+凉菜", "calories": 280, "protein": 12, "carbs": 35, "fat": 8, "fiber": 6 },\n      { "type": "lunch", "content": "糙米饭+清蒸鱼+炒时蔬", "calories": 450, "protein": 25, "carbs": 48, "fat": 12, "fiber": 8 },\n      { "type": "dinner", "content": "杂粮饭+豆腐汤+凉菜", "calories": 320, "protein": 15, "carbs": 38, "fat": 10, "fiber": 7 }\n    ]},\n    { "day": 2, "meals": [\n      { "type": "breakfast", "content": "全麦面包+牛奶+水果", "calories": 300, "protein": 14, "carbs": 40, "fat": 9, "fiber": 5 },\n      { "type": "lunch", "content": "藜麦饭+鸡胸肉+菠菜", "calories": 460, "protein": 28, "carbs": 45, "fat": 11, "fiber": 9 },\n      { "type": "dinner", "content": "红薯+鸡蛋汤+凉拌黄瓜", "calories": 310, "protein": 16, "carbs": 35, "fat": 8, "fiber": 6 }\n    ]},\n    { "day": 3, "meals": [\n      { "type": "breakfast", "content": "玉米+豆浆+鸡蛋", "calories": 290, "protein": 15, "carbs": 38, "fat": 9, "fiber": 7 },\n      { "type": "lunch", "content": "荞麦面+虾仁+西兰花", "calories": 440, "protein": 26, "carbs": 42, "fat": 10, "fiber": 8 },\n      { "type": "dinner", "content": "山药+蔬菜汤+凉拌木耳", "calories": 300, "protein": 14, "carbs": 36, "fat": 7, "fiber": 8 }\n    ]},\n    { "day": 4, "meals": [\n      { "type": "breakfast", "content": "红枣豆浆+全麦面包+坚果", "calories": 280, "protein": 14, "carbs": 36, "fat": 10, "fiber": 6 },\n      { "type": "lunch", "content": "糙米饭+牛肉+西兰花", "calories": 450, "protein": 28, "carbs": 45, "fat": 12, "fiber": 8 },\n      { "type": "dinner", "content": "南瓜粥+蒸鱼+凉拌木耳", "calories": 320, "protein": 16, "carbs": 38, "fat": 9, "fiber": 7 }\n    ]},\n    { "day": 5, "meals": [\n      { "type": "breakfast", "content": "紫薯粥+鸡蛋+坚果", "calories": 290, "protein": 13, "carbs": 35, "fat": 9, "fiber": 5 },\n      { "type": "lunch", "content": "藜麦饭+鸡胸肉+芦笋", "calories": 440, "protein": 27, "carbs": 44, "fat": 10, "fiber": 7 },\n      { "type": "dinner", "content": "豆腐汤+蒸南瓜+黄瓜", "calories": 280, "protein": 14, "carbs": 32, "fat": 6, "fiber": 6 }\n    ]},\n    { "day": 6, "meals": [\n      { "type": "breakfast", "content": "牛奶+麦片+香蕉", "calories": 310, "protein": 12, "carbs": 42, "fat": 8, "fiber": 4 },\n      { "type": "lunch", "content": "全麦面+三文鱼+青菜", "calories": 460, "protein": 30, "carbs": 46, "fat": 14, "fiber": 9 },\n      { "type": "dinner", "content": "山药泥+蔬菜沙拉", "calories": 250, "protein": 10, "carbs": 30, "fat": 5, "fiber": 8 }\n    ]},\n    { "day": 7, "meals": [\n      { "type": "breakfast", "content": "玉米糊+鸡蛋+小番茄", "calories": 270, "protein": 14, "carbs": 34, "fat": 8, "fiber": 5 },\n      { "type": "lunch", "content": "糙米饭+虾仁+荷兰豆", "calories": 420, "protein": 25, "carbs": 42, "fat": 10, "fiber": 7 },\n      { "type": "dinner", "content": "红薯粥+凉拌黄瓜", "calories": 240, "protein": 8, "carbs": 35, "fat": 4, "fiber": 6 }\n    ]}\n  ]\n}'})}>📥 插入7天模板</button>
                  </div>
                  <textarea className="json-input" rows={10} value={formData.nutrition_data} onChange={e => setFormData({...formData, nutrition_data: e.target.value})} placeholder={'{\n  "days": [\n    { "day": 1, "meals": [...] }\n  ]\n}'} />

                  {showNutritionHelp && (
                    <div style={{ border: '1px solid #e0e0e0', borderRadius: '8px', padding: '12px', backgroundColor: '#f8f9fa', marginTop: '12px', fontSize: '12px', color: '#666' }}>
                      <div style={{fontWeight: 'bold', marginBottom: '8px'}}>📊 营养数据格式说明：</div>
                      <div style={{marginBottom: '6px'}}><span style={{color:'#1890ff'}}>day</span> - 第几天（数字，如：1,2,3...）</div>
                      <div style={{marginBottom: '6px', color:'#888'}}>{"{ \"day\": 1 }"}</div>

                      <div style={{marginBottom: '6px'}}><span style={{color:'#1890ff'}}>type</span> - 餐次（breakfast/lunch/dinner）</div>
                      <div style={{marginBottom: '6px', color:'#888'}}>{"{ \"type\": \"breakfast\" }"}</div>

                      <div style={{marginBottom: '6px'}}><span style={{color:'#1890ff'}}>content</span> - 餐食内容（文字描述）</div>
                      <div style={{marginBottom: '6px', color:'#888'}}>{"{ \"content\": \"燕麦粥+水煮蛋\" }"}</div>

                      <div style={{marginBottom: '6px'}}><span style={{color:'#1890ff'}}>calories</span> - 热量（单位：千卡）</div>
                      <div style={{marginBottom: '6px', color:'#888'}}>{"{ \"calories\": 280 }"}</div>

                      <div style={{marginBottom: '6px'}}><span style={{color:'#1890ff'}}>protein</span> - 蛋白质（单位：克）</div>
                      <div style={{marginBottom: '6px', color:'#888'}}>{"{ \"protein\": 12 }"}</div>

                      <div style={{marginBottom: '6px'}}><span style={{color:'#1890ff'}}>carbs</span> - 碳水化合物（单位：克）</div>
                      <div style={{marginBottom: '6px', color:'#888'}}>{"{ \"carbs\": 35 }"}</div>

                      <div style={{marginBottom: '6px'}}><span style={{color:'#1890ff'}}>fat</span> - 脂肪（单位：克）</div>
                      <div style={{marginBottom: '6px', color:'#888'}}>{"{ \"fat\": 8 }"}</div>

                      <div><span style={{color:'#1890ff'}}>fiber</span> - 膳食纤维（单位：克）</div>
                      <div style={{color:'#888'}}>{"{ \"fiber\": 6 }"}</div>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-foot">
                <button type="button" onClick={() => setShowModal(false)}>取消</button>
                <button type="submit" className="action-btn btn-blue">保存</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 营养数据弹窗 */}
      {showNutritionModal && (
        <div className="modal-mask">
          <div className="modal-box">
            <div className="modal-title">
              <span>营养数据 - {selectedProduct?.title}</span>
              <span className="modal-close" onClick={() => setShowNutritionModal(false)}>×</span>
            </div>
            <div className="modal-content">
              {nutritionData.length === 0 ? (
                <div style={{textAlign: 'center', padding: '40px', color: '#999'}}>暂无营养数据</div>
              ) : (
                <div>
                  {nutritionData.map(meal => (
                    <div key={meal.id} style={{padding: '10px', borderBottom: '1px solid #eee'}}>
                      <div style={{fontWeight: 'bold'}}>第{meal.day_number}天 - {meal.meal_type}</div>
                      <div style={{fontSize: '13px', color: '#666'}}>{meal.meal_content}</div>
                      <div style={{fontSize: '13px', color: '#fa8c16', marginTop: '4px'}}>
                        🔥{meal.calories}千卡 🥩{meal.protein}g 🍚{meal.carbs}g 🌿{meal.fiber}g
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-foot">
              <button onClick={() => setShowNutritionModal(false)}>关闭</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dietary;
